import React, { useCallback, useRef, useEffect, useState } from "react";
import {
  ReactFlow,
  addEdge,
  useNodesState,
  useEdgesState,
  Background,
  Controls,
  MiniMap,
  ReactFlowProvider,
  useReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { useFlowViewModel } from "../viewmodels/FlowViewModel";
import FlowService from "../services/FlowService"; // Import the new service
import { NormalMessageNode } from "./nodes/NormalMessageNode.js";
import { InteractiveMessageNode } from "./nodes/InteractiveMessageNode.js";
import { MediaMessageNode } from "./nodes/MediaMessageNode.js";
import { RowNode } from "./nodes/RowNode.js";
import {
  Button,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
} from "@mui/material";
import { Download, Upload, Save, FolderOpen } from "@mui/icons-material";

// Add custom styles to prevent overflow issues
const flowStyles = `
  .react-flow__node {
    z-index: 10 !important;
  }
  .react-flow__node:hover {
    z-index: 20 !important;
  }
  .react-flow__node.react-flow__node-selected {
    z-index: 30 !important;
  }
  .react-flow__handle {
    z-index: 40 !important;
  }
`;

// Inject styles
if (typeof document !== "undefined") {
  const styleElement = document.createElement("style");
  styleElement.textContent = flowStyles;
  document.head.appendChild(styleElement);
}

const nodeTypes = {
  normalMessage: NormalMessageNode,
  interactiveMessage: InteractiveMessageNode,
  mediaMessage: MediaMessageNode,
  rowNode: RowNode,
};

console.log("Available node types:", Object.keys(nodeTypes));

// Inner component that uses useReactFlow
const FlowCanvasInner = ({
  viewModel,
  reactFlowWrapper,
  nodes,
  setNodes,
  edges,
  setEdges,
  onNodesChange,
  onEdgesChange,
  chatbotId,
}) => {
  const { fitView, setViewport, getViewport } = useReactFlow();

  const [childNodes, setChildNodes] = useState(new Map());

  // Import/Export state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  // const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [importData, setImportData] = useState("");
  const [templateData, setTemplateData] = useState(null);
  const [templateId, setTemplateId] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  // const fileInputRef = useRef(null);

  // NEW: Save flow function
  const handleSaveFlow = async () => {
    try {
      const nodes = viewModel.getNodes();

      console.log("=== EXPORTING FLOW ===", {
        nodeCount: nodes.length,
        mediaMessageNodes: nodes
          .filter((n) => n.type === "mediaMessage")
          .map((n) => ({
            id: n.id,
            filePath: n.data?.filePath,
            fileName: n.data?.fileName,
            mediaType: n.data?.mediaType,
          })),
      });

      const flowData = {
        nodes: nodes.map((node) => {
          let transformedNode = transformLabelsForExport(node);
          transformedNode = transformSectionsForExport(transformedNode);
          return {
            ...transformedNode,
            isUpdated:
              transformedNode.isUpdated !== undefined
                ? transformedNode.isUpdated
                : false,
          };
        }),
        edges: viewModel.getEdges(),
        viewport: getViewport(),
        metadata: {
          version: "1.0",
          exportedAt: new Date().toISOString(),
          nodeCount: nodes.length,
          edgeCount: viewModel.getEdges().length,
        },
      };

      console.log("=== SAVING FLOW... ===", {
        ...flowData,
        nodesWithFiles: flowData.nodes.filter(
          (n) =>
            n.type === "mediaMessage" && n.data.selectedFile instanceof File
        ),
      });

      // Use the service to save the flow. It handles file uploads automatically.
      const response = await FlowService.saveFlow(flowData, chatbotId, templateId);
      console.log("=== FLOW SAVED, RESPONSE FROM BACKEND ===", response);
      const savedFlow = JSON.parse(response)
      viewModel.clearFlow();

      savedFlow.nodes.forEach((node) => {
        // The backend should have replaced the file with a URL.
        // We ensure data is an object.
        node.data = node.data || {};
        viewModel.service.model.addNode(node);
      });

      savedFlow.edges.forEach((edge) => {
        viewModel.service.model.addEdge(edge);
      });

      // Notify the rest of the app that the model has changed.
      viewModel.notifyListeners();

      // Restore the viewport
      if (savedFlow.viewport) {
        setViewport(savedFlow.viewport);
      }

      setSnackbar({
        open: true,
        message: "Flow saved successfully!",
        severity: "success",
      });
    } catch (error) {
      console.error("Failed to save flow:", error);
      setSnackbar({
        open: true,
        message: "Failed to save flow: " + (error.message || "Unknown error"),
        severity: "error",
      });
    }
  };
  // Child node management functions
  const handleDeleteRowNode = useCallback(
    (rowNodeId, sectionIndex, rowIndex) => {
      console.log("=== DELETE ROW NODE ===", {
        rowNodeId,
        sectionIndex,
        rowIndex,
      });

      // Remove the row node from the flow
      onNodesChange([{ id: rowNodeId, type: "remove" }]);

      // Remove edges connected to this row node
      const edgesToRemove = edges.filter(
        (edge) => edge.source === rowNodeId || edge.target === rowNodeId
      );
      if (edgesToRemove.length > 0) {
        onEdgesChange(
          edgesToRemove.map((edge) => ({ id: edge.id, type: "remove" }))
        );
      }

      // Update child nodes map
      setChildNodes((prev) => {
        const updated = new Map(prev);
        for (const [parentId, childNodeList] of updated.entries()) {
          const filtered = childNodeList.filter((n) => n.id !== rowNodeId);
          if (filtered.length === 0) {
            updated.delete(parentId);
          } else {
            updated.set(parentId, filtered);
          }
        }
        return updated;
      });

      // Remove the row from the parent node's data
      const parentNodeId = rowNodeId.split("-row-")[0];
      const parentNode = nodes.find((n) => n.id === parentNodeId);
      if (parentNode && parentNode.data.sections) {
        const updatedSections = [...parentNode.data.sections];
        if (
          updatedSections[sectionIndex] &&
          updatedSections[sectionIndex].rows
        ) {
          updatedSections[sectionIndex].rows.splice(rowIndex, 1);
          // Update the parent node's sections
          viewModel.updateNode(parentNodeId, { sections: updatedSections });
        }
      }
    },
    [edges, nodes, onNodesChange, onEdgesChange, viewModel]
  );

  const handleSectionsChange = useCallback(
    (parentNodeId, sections, parentPosition) => {
      console.log("=== SECTIONS CHANGE ===", {
        parentNodeId,
        sectionsCount: sections.length,
        parentPosition,
      });

      // Find the parent node to get its position if not provided
      const parentNode = nodes.find((n) => n.id === parentNodeId);
      if (!parentNode) {
        console.warn("Parent node not found:", parentNodeId);
        return;
      }

      // Use provided position or fallback to parent node position
      const position = parentPosition || parentNode.position;
      if (
        !position ||
        typeof position.x === "undefined" ||
        typeof position.y === "undefined"
      ) {
        console.warn(
          "Invalid position for parent node:",
          parentNodeId,
          position
        );
        return;
      }

      // Remove existing child nodes for this parent
      const existingChildNodes = childNodes.get(parentNodeId) || [];
      const existingIds = existingChildNodes.map((n) => n.id);

      if (existingIds.length > 0) {
        const nodesToRemove = nodes.filter((n) => existingIds.includes(n.id));
        if (nodesToRemove.length > 0) {
          onNodesChange(
            nodesToRemove.map((n) => ({ id: n.id, type: "remove" }))
          );
        }
      }

      // Generate new child row nodes
      const newChildNodes = [];
      sections.forEach((section, sectionIndex) => {
        if (section.rows && Array.isArray(section.rows)) {
          section.rows.forEach((row, rowIndex) => {
            const rowNodeId = `${parentNodeId}-row-${sectionIndex}-${rowIndex}`;
            const rowNode = {
              id: rowNodeId,
              type: "rowNode",
              position: {
                x: position.x + 200 + rowIndex * 20, // Offset from parent
                y: position.y + 100 + sectionIndex * 60 + rowIndex * 50, // Stack rows vertically
              },
              data: {
                title: row.title || `Row ${rowIndex + 1}`,
                description: row.description || "No description",
                sectionIndex,
                rowIndex,
                parentNodeId,
                onDeleteRow: (rowId) => {
                  handleDeleteRowNode(rowId, sectionIndex, rowIndex);
                },
                edges: edges.filter(
                  (e) => e.source === rowNodeId || e.target === rowNodeId
                ),
              },
              parentNode: parentNodeId,
            };
            newChildNodes.push(rowNode);
          });
        }
      });

      // Update child nodes map
      setChildNodes((prev) => {
        const updated = new Map(prev);
        if (newChildNodes.length === 0) {
          updated.delete(parentNodeId);
        } else {
          updated.set(parentNodeId, newChildNodes);
        }
        return updated;
      });

      // Add new child nodes to the flow
      if (newChildNodes.length > 0) {
        console.log("=== CREATING ROW NODES ===", {
          count: newChildNodes.length,
          nodes: newChildNodes.map((n) => ({
            id: n.id,
            type: n.type,
            position: n.position,
          })),
        });
        onNodesChange(newChildNodes.map((n) => ({ item: n, type: "add" })));
      }
    },
    [childNodes, nodes, edges, onNodesChange, handleDeleteRowNode]
  );

  // Add logging for node position tracking
  const logNodePositions = useCallback(
    (nodes, action = "update") => {
      console.log(`=== NODE POSITIONS ${action.toUpperCase()} ===`);
      nodes.forEach((node, index) => {
        console.log(`Node ${index + 1} (${node.id}):`, {
          x: node.position.x,
          y: node.position.y,
          type: node.type,
          selected: node.selected,
          zIndex: node.style?.zIndex || "default",
        });
      });
      console.log("Canvas wrapper dimensions:", {
        width: reactFlowWrapper.current?.offsetWidth,
        height: reactFlowWrapper.current?.offsetHeight,
        scrollLeft: reactFlowWrapper.current?.scrollLeft,
        scrollTop: reactFlowWrapper.current?.scrollTop,
      });
      console.log("================================");
    },
    [reactFlowWrapper]
  );

  // Sync ViewModel state with React Flow state
  useEffect(() => {
    const flowNodes = viewModel.getNodes();
    const flowEdges = viewModel.getEdges();

    console.log(
      "Syncing state - Nodes:",
      flowNodes.length,
      "Edges:",
      flowEdges.length
    );

    console.log("=== REACT FLOW SYNC DETAILS ===", {
      flowNodes: flowNodes.map((n) => ({ id: n.id, type: n.type })),
      flowEdges: flowEdges.map((e) => ({
        id: e.id,
        source: e.source,
        target: e.target,
        sourceHandle: e.sourceHandle,
        targetHandle: e.targetHandle,
      })),
    });

    // Add delete handlers to nodes
    const nodesWithHandlers = flowNodes.map((viewModelNode) => {
      return {
        ...viewModelNode,
        data: {
          ...viewModelNode.data,
          onDelete: (id) => {
            console.log("Direct delete called for node:", id);
            viewModel.deleteNode(id);
          },
          edges: flowEdges,
        },
      };
    });

    setNodes(nodesWithHandlers);
    setEdges(flowEdges);

    console.log("=== REACT FLOW STATE SET ===", {
      nodesSet: nodesWithHandlers.length,
      edgesSet: flowEdges.length,
      edgeDetails: flowEdges.map((e) => ({
        id: e.id,
        source: e.source,
        target: e.target,
        sourceHandle: e.sourceHandle,
        targetHandle: e.targetHandle,
      })),
    });
    logNodePositions(nodesWithHandlers, "sync");
  }, [viewModel, setNodes, setEdges, logNodePositions]);

  // Force re-render when ViewModel state changes
  useEffect(() => {
    console.log("Setting up ViewModel subscription...");

    // Initial fit view only if nodes exist and we're not already positioned
    if (nodes.length > 0) {
      const viewport = getViewport();
      const isAtDefaultPosition =
        viewport.x === 0 && viewport.y === 0 && viewport.zoom === 1;

      if (isAtDefaultPosition) {
        setTimeout(() => {
          console.log("Initial fit view for default position");
          fitView({ padding: 0.1 });
        }, 200);
      }
    }
    const unsubscribe = viewModel.subscribe(() => {
      console.log("ViewModel subscription triggered!");
      const flowNodes = viewModel.getNodes();
      const flowEdges = viewModel.getEdges();
      console.log(
        "ViewModel state changed - Nodes:",
        flowNodes.length,
        "Edges:",
        flowEdges.length
      );
      console.log(
        "ViewModel node positions:",
        flowNodes.map((n) => ({ id: n.id, position: n.position }))
      );
      console.log(
        "Current React Flow node positions:",
        nodes.map((n) => ({ id: n.id, position: n.position }))
      );

      // Always update edges to ensure nodes have current connection state
      // Only update node positions if node count changes to prevent overwriting during drag
      if (flowNodes.length !== nodes.length) {
        console.log("Node count changed, updating React Flow state");
        console.log(
          "Current React Flow nodes:",
          nodes.map((n) => ({ id: n.id, position: n.position }))
        );
        console.log(
          "ViewModel nodes:",
          flowNodes.map((n) => ({ id: n.id, position: n.position }))
        );

        const nodesWithHandlers = flowNodes.map((viewModelNode) => {
          const nodeData = {
            ...viewModelNode.data,
            onDelete: (id) => {
              console.log("Direct delete called for node:", id);
              viewModel.deleteNode(id);
            },
            edges: flowEdges,
            // Force re-render by adding a timestamp
            lastEdgeUpdate: Date.now(),
          };

          if (viewModelNode.type === "mediaMessage") {
            console.log("=== PROCESSING MEDIA MESSAGE NODE ===", {
              id: viewModelNode.id,
              originalData: viewModelNode.data,
              processedData: nodeData,
              hasFilePath: !!nodeData.filePath,
              filePathValue: nodeData.filePath,
              hasOnChangeFilePath: !!nodeData.onChangeFilePath,
            });
            // Wire up the onChange handlers for MediaMessageNode
            nodeData.onChangeFile = (id, file) =>
              viewModel.updateNode(id, { selectedFile: file });
            nodeData.onChangeCaption = (id, caption) =>
              viewModel.updateNode(id, { caption });
            nodeData.onChangeMediaType = (id, mediaType) =>
              viewModel.updateNode(id, { mediaType });
            nodeData.onChangeFileName = (id, fileName) =>
              viewModel.updateNode(id, { fileName });
            nodeData.onChangeFilePath = (id, filePath) =>
              viewModel.updateNode(id, { filePath });
            nodeData.onChangePreviewUrl = (id, previewUrl) =>
              viewModel.updateNode(id, { previewUrl });
          } else if (viewModelNode.type === "interactiveMessage") {
            console.log("=== PROCESSING INTERACTIVE MESSAGE NODE ===", {
              id: viewModelNode.id,
              originalData: viewModelNode.data,
              processedData: nodeData,
            });
            // Wire up the onChange handlers for InteractiveMessageNode
            nodeData.onChangeHeader = (id, value) =>
              viewModel.updateNode(id, { header: value });
            nodeData.onChangeBody = (id, value) =>
              viewModel.updateNode(id, { body: value });
            nodeData.onChangeFooter = (id, value) =>
              viewModel.updateNode(id, { footer: value });
            nodeData.onChangeLabels = (id, value) =>
              viewModel.updateNode(id, { labels: value });
            nodeData.onChangeSections = (id, value) =>
              viewModel.updateNode(id, { sections: value });
            nodeData.onChangeListHeader = (id, value) =>
              viewModel.updateNode(id, { listHeader: value });
            nodeData.onChangeMessageType = (id, value) =>
              viewModel.updateNode(id, { messageType: value });
          } else if (viewModelNode.type === "normalMessage") {
            console.log("=== PROCESSING NORMAL MESSAGE NODE ===", {
              id: viewModelNode.id,
              originalData: viewModelNode.data,
              processedData: nodeData,
            });
            // Wire up the onChange handlers for NormalMessageNode
            nodeData.onChangeText = (id, value) =>
              viewModel.updateNode(id, { text: value });
          }

          return {
            ...viewModelNode,
            data: nodeData,
          };
        });

        console.log(
          "Setting nodes with handlers:",
          nodesWithHandlers.map((n) => ({ id: n.id, position: n.position }))
        );
        setNodes(nodesWithHandlers);
        setEdges(flowEdges);

        // Only fit view if no nodes exist yet (first node)
        if (nodesWithHandlers.length === 1 && nodes.length === 0) {
          setTimeout(() => {
            console.log("Fitting view to first node");
            fitView({ padding: 0.1 });
          }, 100);
        }
      } else {
        console.log("Node count unchanged, checking if edge update is needed");
        console.log(
          "Current nodes positions:",
          nodes.map((n) => ({ id: n.id, position: n.position }))
        );

        // Check if edges actually changed to avoid unnecessary updates
        const currentEdgeIds = edges.map((e) => e.id).sort();
        const newEdgeIds = flowEdges.map((e) => e.id).sort();
        const edgesChanged =
          JSON.stringify(currentEdgeIds) !== JSON.stringify(newEdgeIds);

        console.log("Edge change detection:", {
          currentEdgeIds,
          newEdgeIds,
          edgesChanged,
          currentEdgeCount: edges.length,
          newEdgeCount: flowEdges.length,
          countMismatch: flowEdges.length !== edges.length,
        });

        // Always update nodes when edges change OR when ViewModel data changes
        if (edgesChanged || flowEdges.length !== edges.length) {
          console.log("Edges changed, updating nodes with new edge data");

          // Update nodes with new edges data without changing positions
          const nodesWithUpdatedEdges = nodes.map((node) => {
            const viewModelNode = flowNodes.find((vn) => vn.id === node.id);
            if (viewModelNode) {
              console.log(`Preserving position for node ${node.id}:`, {
                reactFlowPosition: node.position,
                viewModelPosition: viewModelNode.position,
                positionsMatch:
                  JSON.stringify(node.position) ===
                  JSON.stringify(viewModelNode.position),
              });

              // Always use React Flow position, never ViewModel position
              const finalPosition = node.position;

              return {
                ...node,
                // Always preserve current position from React Flow, never use ViewModel position
                position: finalPosition,
                data: {
                  ...node.data,
                  edges: flowEdges,
                  // Force re-render by adding a timestamp
                  lastEdgeUpdate: Date.now(),
                },
              };
            }
            return node;
          });

          console.log("Updating nodes with new edges data:", {
            nodesCount: nodesWithUpdatedEdges.length,
            edgesCount: flowEdges.length,
          });

          setNodes(nodesWithUpdatedEdges);
          setEdges(flowEdges);
        } else {
          // Check if any node data has changed in the ViewModel
          const hasDataChanges = nodes.some((node) => {
            const viewModelNode = flowNodes.find((vn) => vn.id === node.id);
            if (!viewModelNode) return false;

            // Compare node data (excluding handlers and position)
            const {
              position: _,
              onDelete: __,
              edges: ___,
              lastEdgeUpdate: ____,
              ...nodeData
            } = node.data;
            const {
              position: _____,
              onDelete: ______,
              edges: _______,
              lastEdgeUpdate: ________,
              ...viewModelData
            } = viewModelNode.data;

            return JSON.stringify(nodeData) !== JSON.stringify(viewModelData);
          });

          if (hasDataChanges) {
            console.log("Node data changed in ViewModel, updating nodes");

            // Update nodes with new ViewModel data while preserving positions
            const nodesWithUpdatedData = nodes.map((node) => {
              const viewModelNode = flowNodes.find((vn) => vn.id === node.id);
              if (viewModelNode) {
                const nodeData = {
                  ...viewModelNode.data, // Use ViewModel data
                  onDelete: (id) => {
                    console.log("Direct delete called for node:", id);
                    viewModel.deleteNode(id);
                  },
                  edges: flowEdges,
                  lastEdgeUpdate: Date.now(),
                };

                // Wire up handlers for different node types
                if (viewModelNode.type === "mediaMessage") {
                  nodeData.onChangeFile = (id, file) =>
                    viewModel.updateNode(id, { selectedFile: file });
                  nodeData.onChangeCaption = (id, caption) =>
                    viewModel.updateNode(id, { caption });
                  nodeData.onChangeMediaType = (id, mediaType) =>
                    viewModel.updateNode(id, { mediaType });
                  nodeData.onChangeFileName = (id, fileName) =>
                    viewModel.updateNode(id, { fileName });
                  nodeData.onChangeFilePath = (id, filePath) =>
                    viewModel.updateNode(id, { filePath });
                  nodeData.onChangePreviewUrl = (id, previewUrl) =>
                    viewModel.updateNode(id, { previewUrl });
                } else if (viewModelNode.type === "interactiveMessage") {
                  nodeData.onChangeHeader = (id, value) =>
                    viewModel.updateNode(id, { header: value });
                  nodeData.onChangeBody = (id, value) =>
                    viewModel.updateNode(id, { body: value });
                  nodeData.onChangeFooter = (id, value) =>
                    viewModel.updateNode(id, { footer: value });
                  nodeData.onChangeLabels = (id, value) =>
                    viewModel.updateNode(id, { labels: value });
                  nodeData.onChangeSections = (id, value) =>
                    viewModel.updateNode(id, { sections: value });
                  nodeData.onChangeListHeader = (id, value) =>
                    viewModel.updateNode(id, { listHeader: value });
                  nodeData.onChangeMessageType = (id, value) =>
                    viewModel.updateNode(id, { messageType: value });
                } else if (viewModelNode.type === "normalMessage") {
                  nodeData.onChangeText = (id, value) =>
                    viewModel.updateNode(id, { text: value });
                }

                return {
                  ...node,
                  position: node.position, // Preserve React Flow position
                  data: nodeData,
                };
              }
              return node;
            });

            setNodes(nodesWithUpdatedData);
            setEdges(flowEdges);
          } else {
            console.log("No edge changes detected, skipping node update");
          }
        }
      }
    });

    return unsubscribe;
  }, [viewModel, setNodes, setEdges, nodes.length, edges, nodes, fitView]);

  const onConnect = useCallback(
    (params) => {
      console.log("=== FlowCanvas onConnect ===", params);

      try {
        viewModel.addEdge(
          params.source,
          params.target,
          params.sourceHandle,
          params.targetHandle
        );
        setEdges((eds) => addEdge(params, eds));
      } catch (error) {
        console.error("Failed to create connection:", error);
      }
    },
    [viewModel, setEdges]
  );

  const handleNodesChange = useCallback(
    (changes) => {
      console.log("=== REACTFLOW NODES CHANGE ===");
      console.log("Changes:", changes);
      console.log(
        "Current nodes before change:",
        nodes.map((n) => ({ id: n.id, position: n.position, type: n.type }))
      );

      // Get canvas dimensions for boundary checking
      const canvasBounds = reactFlowWrapper.current?.getBoundingClientRect();
      console.log("Canvas bounds:", canvasBounds);

      // Handle different types of node changes
      changes.forEach((change) => {
        console.log(
          `Processing change type: ${change.type} for node: ${change.id}`
        );

        switch (change.type) {
          case "remove":
            console.log("Node deleted via ReactFlow:", change.id);
            viewModel.deleteNode(change.id);
            break;
          case "position":
            if (change.position) {
              console.log("=== NODE POSITION CHANGE ===");
              console.log("Node ID:", change.id);
              console.log("New position:", change.position);

              // Check if node is near boundaries and constrain if necessary
              if (canvasBounds) {
                const nodeWidth = 320; // Approximate node width
                const nodeHeight = 280; // Approximate node height
                const padding = 50; // Reduced padding for less aggressive constraining

                // Get current viewport to check zoom level
                const viewport = getViewport();
                const isZoomedOut = viewport.zoom < 0.8;

                // Use dynamic padding based on zoom level
                const dynamicPadding = isZoomedOut ? 20 : padding;

                // Calculate constrained position
                let constrainedPosition = { ...change.position };

                // Only constrain if not too far zoomed out
                if (!isZoomedOut) {
                  // Constrain to left boundary
                  if (change.position.x < dynamicPadding) {
                    constrainedPosition.x = dynamicPadding;
                  }

                  // Constrain to right boundary
                  if (
                    change.position.x >
                    canvasBounds.width - nodeWidth - dynamicPadding
                  ) {
                    constrainedPosition.x =
                      canvasBounds.width - nodeWidth - dynamicPadding;
                  }

                  // Constrain to top boundary
                  if (change.position.y < dynamicPadding) {
                    constrainedPosition.y = dynamicPadding;
                  }

                  // Constrain to bottom boundary
                  if (
                    change.position.y >
                    canvasBounds.height - nodeHeight - dynamicPadding
                  ) {
                    constrainedPosition.y =
                      canvasBounds.height - nodeHeight - dynamicPadding;
                  }
                }

                const isNearLeftBoundary = change.position.x < dynamicPadding;
                const isNearRightBoundary =
                  change.position.x >
                  canvasBounds.width - nodeWidth - dynamicPadding;
                const isNearTopBoundary = change.position.y < dynamicPadding;
                const isNearBottomBoundary =
                  change.position.y >
                  canvasBounds.height - nodeHeight - dynamicPadding;

                console.log("Boundary check:", {
                  isNearLeftBoundary,
                  isNearRightBoundary,
                  isNearTopBoundary,
                  isNearBottomBoundary,
                  canvasWidth: canvasBounds.width,
                  canvasHeight: canvasBounds.height,
                  nodeWidth,
                  nodeHeight,
                  originalPosition: change.position,
                  constrainedPosition,
                });

                if (
                  isNearLeftBoundary ||
                  isNearRightBoundary ||
                  isNearTopBoundary ||
                  isNearBottomBoundary
                ) {
                  console.warn(
                    "⚠️ NODE NEAR BOUNDARY - Constraining position!"
                  );
                  console.warn("Original position:", change.position);
                  console.warn("Constrained position:", constrainedPosition);
                  // eslint-disable-next-line no-use-before-define
                  console.warn("Canvas bounds:", canvasBounds);

                  // Use the constrained position instead
                  change.position = constrainedPosition;

                  // Auto-pan viewport if node is near boundary (only for small adjustments)
                  const viewport = getViewport();

                  if (canvasBounds) {
                    let newViewport = { ...viewport };
                    let shouldPan = false;
                    const panAmount = 20; // Reduced pan amount for subtle movement

                    // Only pan if we're not too far from the boundary (within 200px)
                    const maxPanDistance = 200;

                    // Pan down if node is near bottom and not too far out
                    if (
                      isNearBottomBoundary &&
                      change.position.y < maxPanDistance
                    ) {
                      newViewport.y -= panAmount;
                      shouldPan = true;
                    }

                    // Pan up if node is near top and not too far out
                    if (
                      isNearTopBoundary &&
                      change.position.y > -maxPanDistance
                    ) {
                      newViewport.y += panAmount;
                      shouldPan = true;
                    }

                    // Pan right if node is near left and not too far out
                    if (
                      isNearLeftBoundary &&
                      change.position.x < maxPanDistance
                    ) {
                      newViewport.x -= panAmount;
                      shouldPan = true;
                    }

                    // Pan left if node is near right and not too far out
                    if (
                      isNearRightBoundary &&
                      change.position.x > -maxPanDistance
                    ) {
                      newViewport.x += panAmount;
                      shouldPan = true;
                    }

                    if (shouldPan) {
                      console.log("Subtle auto-panning viewport:", {
                        old: viewport,
                        new: newViewport,
                        reason: "boundary approach",
                      });
                      setViewport(newViewport, { duration: 300 }); // Smooth transition
                    }
                  }
                }
              }

              // Update the node position using the existing updateNode method
              const node = viewModel.getNodeById(change.id);
              if (node) {
                console.log("Updating node in ViewModel:", {
                  id: change.id,
                  oldPosition: node.position,
                  newPosition: change.position,
                });
                viewModel.updateNode(change.id, {
                  ...node.data,
                  position: change.position,
                });
              } else {
                console.error("❌ Node not found in ViewModel:", change.id);
              }
            }
            break;
          case "dimensions":
            // Node dimensions changed (e.g., content added/removed)
            // This is normal and doesn't require any special handling
            console.log("Node dimensions changed:", {
              id: change.id,
              dimensions: change.dimensions,
            });
            break;
          case "select":
            // Node selection changed
            console.log("Node selection changed:", {
              id: change.id,
              selected: change.selected,
            });
            break;
          default:
            console.log("Unhandled node change type:", change.type);
        }
      });

      console.log(
        "Current nodes after change:",
        nodes.map((n) => ({ id: n.id, position: n.position, type: n.type }))
      );
      console.log("=== END NODES CHANGE ===");

      onNodesChange(changes);
    },
    [
      viewModel,
      onNodesChange,
      nodes,
      reactFlowWrapper,
      getViewport,
      setViewport,
    ]
  );

  const handleEdgesChange = useCallback(
    (changes) => {
      console.log("ReactFlow edges change:", changes);

      // Handle edge deletions to update ViewModel
      changes.forEach((change) => {
        if (change.type === "remove") {
          console.log("Edge deleted via ReactFlow:", change.id);
          console.log(
            "Current node positions before edge deletion:",
            nodes.map((n) => ({ id: n.id, position: n.position }))
          );
          viewModel.deleteEdge(change.id);
          console.log("Edge deletion completed, ViewModel should be updated");
        }
      });

      onEdgesChange(changes);
    },
    [viewModel, onEdgesChange, nodes]
  );

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      const reactFlowBounds = reactFlowWrapper.current?.getBoundingClientRect();
      const type = event.dataTransfer.getData("application/reactflow");

      if (typeof type === "undefined" || !type) {
        return;
      }

      if (reactFlowBounds) {
        // Use reliable positioning that works consistently
        const position = {
          x: event.clientX - reactFlowBounds.left,
          y: event.clientY - reactFlowBounds.top,
        };

        console.log("Adding node:", {
          type,
          position,
          screenX: event.clientX,
          screenY: event.clientY,
          reactFlowBounds: {
            left: reactFlowBounds.left,
            top: reactFlowBounds.top,
          },
        });

        try {
          // Add child node management callbacks for interactive message nodes
          const nodeData =
            type === "interactiveMessage"
              ? {
                  onSectionsChange: handleSectionsChange,
                  onDeleteRowNode: handleDeleteRowNode,
                }
              : {};

          const newNode = viewModel.addNode(type, position, nodeData);
          console.log("Node created successfully:", newNode);
          console.log("New node position:", newNode.position);
          console.log(
            "All nodes after adding:",
            viewModel
              .getNodes()
              .map((n) => ({ id: n.id, position: n.position }))
          );

          // Clear the drag data
          event.dataTransfer.clearData();
        } catch (error) {
          console.error("Failed to add node:", error);
        }
      } else {
        console.error("React Flow not ready:", {
          reactFlowBounds: !!reactFlowBounds,
        });
      }
    },
    [viewModel, reactFlowWrapper]
  );

  // Helper function to transform labels array for interactive_message nodes
  const transformLabelsForExport = (node) => {
    if (
      node.type === "interactiveMessage" &&
      node.data &&
      node.data.labels &&
      Array.isArray(node.data.labels) &&
      node.data.labels.length > 0
    ) {
      // Check if labels are already in the new format (objects with label_id)
      const isNewFormat = node.data.labels.every(
        (label) => typeof label === "object" && label.label && label.label_id
      );

      if (!isNewFormat) {
        // Transform string array to object array
        const transformedLabels = node.data.labels.map((label, index) => ({
          label: label,
          label_id: `btn_${Date.now()}_${Math.random().toString(36).substr(2, 9)}_${index}`,
        }));

        return {
          ...node,
          data: {
            ...node.data,
            labels: transformedLabels,
          },
        };
      }
    }
    return node;
  };

  // Helper function to transform sections for interactive_message nodes
  const transformSectionsForExport = (node) => {
    if (
      node.type === "interactiveMessage" &&
      node.data &&
      node.data.sections &&
      Array.isArray(node.data.sections)
    ) {
      // Check if sections are already in the new format (object with header and items)
      const isNewFormat =
        node.data.sections &&
        typeof node.data.sections === "object" &&
        !Array.isArray(node.data.sections) &&
        node.data.sections.header !== undefined &&
        node.data.sections.items !== undefined;

      if (!isNewFormat) {
        // Transform array to object with header and items
        const transformedSections = {
          header: node.data.listHeader || "",
          items: node.data.sections,
        };

        return {
          ...node,
          data: {
            ...node.data,
            sections: transformedSections,
          },
        };
      }
    }
    return node;
  };

  // Helper function to transform labels for import (reverse transformation)
  const transformLabelsForImport = (node) => {
    if (
      node.type === "interactiveMessage" &&
      node.data &&
      node.data.labels &&
      Array.isArray(node.data.labels) &&
      node.data.labels.length > 0
    ) {
      // Check if labels are in the new format (objects with label_id)
      const isNewFormat = node.data.labels.every(
        (label) => typeof label === "object" && label.label && label.label_id
      );

      if (isNewFormat) {
        // Transform object array back to string array
        const transformedLabels = node.data.labels.map((label) => label.label);

        return {
          ...node,
          data: {
            ...node.data,
            labels: transformedLabels,
          },
        };
      }
    }
    return node;
  };

  // Helper function to transform sections for import (reverse transformation)
  const transformSectionsForImport = (node) => {
    if (
      node.type === "interactiveMessage" &&
      node.data &&
      node.data.sections &&
      typeof node.data.sections === "object" &&
      !Array.isArray(node.data.sections) &&
      node.data.sections.header !== undefined &&
      node.data.sections.items !== undefined
    ) {
      // Transform object back to array and extract header
      const transformedSections = node.data.sections.items || [];
      const listHeader = node.data.sections.header || "";

      return {
        ...node,
        data: {
          ...node.data,
          sections: transformedSections,
          listHeader: listHeader,
        },
      };
    }
    return node;
  };

  // Import/Export functions
  const exportFlow = () => {
    try {
      const nodes = viewModel.getNodes();

      console.log("=== EXPORTING FLOW ===", {
        nodeCount: nodes.length,
        mediaMessageNodes: nodes
          .filter((n) => n.type === "mediaMessage")
          .map((n) => ({
            id: n.id,
            filePath: n.data?.filePath,
            fileName: n.data?.fileName,
            mediaType: n.data?.mediaType,
          })),
      });

      const flowData = {
        nodes: nodes.map((node) => {
          let transformedNode = transformLabelsForExport(node);
          transformedNode = transformSectionsForExport(transformedNode);
          return {
            ...transformedNode,
            isUpdated:
              transformedNode.isUpdated !== undefined
                ? transformedNode.isUpdated
                : false,
          };
        }),
        edges: viewModel.getEdges(),
        viewport: getViewport(),
        metadata: {
          version: "1.0",
          exportedAt: new Date().toISOString(),
          nodeCount: nodes.length,
          edgeCount: viewModel.getEdges().length,
        },
      };

      const jsonString = JSON.stringify(flowData, null, 2);
      const blob = new Blob([jsonString], { type: "application/json" });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `flow-export-${
        new Date().toISOString().split("T")[0]
      }.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setSnackbar({
        open: true,
        message: "Flow exported successfully!",
        severity: "success",
      });
    } catch (error) {
      console.error("Export error:", error);
      setSnackbar({
        open: true,
        message: "Failed to export flow: " + error.message,
        severity: "error",
      });
    }
  };

  function getTemplateData() {
    try {
      console.log("=== FLOWCANVAS template START ===", {
        importDataLength: importData.length,
      });

      const flowData = JSON.parse(templateData);
      // Validate the imported data
      if (!flowData.nodes || !Array.isArray(flowData.nodes)) {
        throw new Error("Invalid flow data: missing or invalid nodes");
      }

      if (!flowData.edges || !Array.isArray(flowData.edges)) {
        console.warn(
          "No edges found in import data, will use empty edges array"
        );
        flowData.edges = [];
      }

      // Clear current flow
      viewModel.clearFlow();

      // Import nodes with isUpdated attribute
      const importedNodes = [];
      flowData.nodes.forEach((node, index) => {
        try {
          console.log(
            `=== IMPORTING NODE ${index + 1}/${flowData.nodes.length} ===`,
            {
              id: node.id,
              type: node.type,
              hasData: !!node.data,
              position: node.position,
              data: node.data,
            }
          );

          // Apply import transformations to handle exported format
          let transformedNode = transformLabelsForImport(node);
          transformedNode = transformSectionsForImport(transformedNode);

          // Create node with original ID by directly adding to model
          const nodeToAdd = {
            id: transformedNode.id, // Preserve original ID
            type: transformedNode.type,
            position: transformedNode.position,
            data: {
              ...transformedNode.data,
              isUpdated:
                transformedNode.isUpdated !== undefined
                  ? transformedNode.isUpdated
                  : false,
            },
          };

          // Add node directly to the model to preserve ID
          viewModel.service.model.addNode(nodeToAdd);

          // Add proper handlers to the imported node
          const importedNode = viewModel.service.model.getNode(nodeToAdd.id);
          if (importedNode) {
            // Add delete handler
            importedNode.data.onDelete = (id) => {
              console.log("Direct delete called for imported node:", id);
              viewModel.deleteNode(id);
            };

            // Add type-specific handlers
            if (nodeToAdd.type === "normalMessage") {
              importedNode.data.onChangeText = (id, value) =>
                viewModel.updateNode(id, { text: value });
            } else if (nodeToAdd.type === "interactiveMessage") {
              importedNode.data.onChangeHeader = (id, value) =>
                viewModel.updateNode(id, { header: value });
              importedNode.data.onChangeBody = (id, value) =>
                viewModel.updateNode(id, { body: value });
              importedNode.data.onChangeFooter = (id, value) =>
                viewModel.updateNode(id, { footer: value });
              importedNode.data.onChangeLabels = (id, value) =>
                viewModel.updateNode(id, { labels: value });
              importedNode.data.onChangeSections = (id, value) =>
                viewModel.updateNode(id, { sections: value });
              importedNode.data.onChangeListHeader = (id, value) =>
                viewModel.updateNode(id, { listHeader: value });
              importedNode.data.onChangeMessageType = (id, value) =>
                viewModel.updateNode(id, { messageType: value });
            } else if (nodeToAdd.type === "mediaMessage") {
              importedNode.data.onChangeFile = (id, file) =>
                viewModel.updateNode(id, { selectedFile: file });
              importedNode.data.onChangeCaption = (id, caption) =>
                viewModel.updateNode(id, { caption });
              importedNode.data.onChangeMediaType = (id, mediaType) =>
                viewModel.updateNode(id, { mediaType });
              importedNode.data.onChangeFileName = (id, fileName) =>
                viewModel.updateNode(id, { fileName });
              importedNode.data.onChangeFilePath = (id, filePath) =>
                viewModel.updateNode(id, { filePath });
              importedNode.data.onChangePreviewUrl = (id, previewUrl) =>
                viewModel.updateNode(id, { previewUrl });
            }
          }

          console.log(`=== NODE IMPORT RESULT ${index + 1} ===`, {
            originalId: node.id,
            importedId: nodeToAdd.id,
            success: true,
            importedNode: nodeToAdd,
            hasHandlers: !!importedNode?.data?.onDelete,
          });

          importedNodes.push(nodeToAdd);
        } catch (error) {
          console.error(`Failed to import node ${node.id}:`, error);
          throw new Error(`Failed to import node ${node.id}: ${error.message}`);
        }
      });

      // Notify listeners after all nodes are imported
      viewModel.notifyListeners();

      // Verify nodes were imported with correct IDs
      const importedNodesFromModel = viewModel.getNodes();
      console.log("=== NODE IMPORT VERIFICATION ===", {
        expectedNodeIds: flowData.nodes.map((n) => n.id),
        actualNodeIds: importedNodesFromModel.map((n) => n.id),
        allMatch: flowData.nodes.every(
          (node, index) => node.id === importedNodesFromModel[index]?.id
        ),
      });

      // Import edges
      const importedEdges = [];
      console.log("=== STARTING EDGE IMPORT ===", {
        totalEdges: flowData.edges.length,
        availableNodes: viewModel.getNodes().map((n) => n.id),
      });

      flowData.edges.forEach((edge, index) => {
        try {
          console.log(
            `=== IMPORTING EDGE ${index + 1}/${flowData.edges.length} ===`,
            {
              id: edge.id,
              source: edge.source,
              target: edge.target,
              sourceHandle: edge.sourceHandle,
              targetHandle: edge.targetHandle,
            }
          );

          console.log("=== EDGE IMPORT DETAILS ===", {
            edgeId: edge.id,
            source: edge.source,
            target: edge.target,
            sourceHandle: edge.sourceHandle,
            targetHandle: edge.targetHandle,
            edgeType: edge.type,
          });

          // Check if source and target nodes exist
          const sourceNode = viewModel.getNodeById(edge.source);
          const targetNode = viewModel.getNodeById(edge.target);

          console.log("=== EDGE NODE VALIDATION ===", {
            sourceExists: !!sourceNode,
            targetExists: !!targetNode,
            sourceNode: sourceNode,
            targetNode: targetNode,
          });

          // Create edge with original ID
          const edgeToAdd = {
            id: edge.id, // Preserve original ID
            source: edge.source,
            target: edge.target,
            sourceHandle: edge.sourceHandle || null,
            targetHandle: edge.targetHandle || null,
            type: edge.type || "default",
            animated: edge.animated || false,
            style: edge.style || {},
          };

          // Add edge directly to the model to preserve ID
          viewModel.service.model.addEdge(edgeToAdd);

          // Verify edge was added to model
          const allEdges = viewModel.service.model.getAllEdges();
          const addedEdge = allEdges.find((e) => e.id === edge.id);

          console.log("=== EDGE IMPORT RESULT ===", {
            originalEdgeId: edge.id,
            importedEdge: edgeToAdd,
            success: true,
            edgeAddedToModel: !!addedEdge,
            totalEdgesInModel: allEdges.length,
            allEdgeIds: allEdges.map((e) => e.id),
          });

          importedEdges.push(edgeToAdd);
        } catch (error) {
          console.warn(`Failed to import edge ${edge.id}:`, error);
        }
      });

      // Notify listeners after all edges are imported
      viewModel.notifyListeners();

      // Final verification
      const finalNodes = viewModel.getNodes();
      const finalEdges = viewModel.getEdges();

      console.log("=== FINAL IMPORT STATE ===", {
        totalNodes: finalNodes.length,
        totalEdges: finalEdges.length,
        nodeIds: finalNodes.map((n) => n.id),
        edgeIds: finalEdges.map((e) => e.id),
        edgesWithConnections: finalEdges.map((e) => ({
          id: e.id,
          source: e.source,
          target: e.target,
          sourceHandle: e.sourceHandle,
          targetHandle: e.targetHandle,
        })),
      });

      // Set viewport if available
      if (flowData.viewport) {
        setViewport(flowData.viewport);
      }

      setImportDialogOpen(false);
      setImportData("");

      setSnackbar({
        open: true,
        message: `Flow imported successfully! ${flowData.nodes.length} nodes, ${flowData.edges.length} edges`,
        severity: "success",
      });
    } catch (error) {
      console.error("Import error:", error);
      setSnackbar({
        open: true,
        message: "Failed to import flow: " + error.message,
        severity: "error",
      });
    }
  }
  useEffect(() => {
    if (chatbotId !== null && chatbotId !== "") {
      FlowService.getFlow(chatbotId).then((res) => {
        if (res) {
          setTemplateData(res.template_json);
          setTemplateId(res.id);
        } else {
          setTemplateData(null);
          setTemplateId(null);
        }
      });
    }
  }, [chatbotId]);

  useEffect(() => {
    if (templateData != null) {
      getTemplateData();
    }
  }, [templateData]);

  const importFlow = () => {
    try {
      console.log("=== FLOWCANVAS IMPORT START ===", {
        importDataLength: importData.length,
      });

      const flowData = JSON.parse(importData);
      console.log("=== PARSED FLOW DATA ===", {
        hasNodes: !!flowData.nodes,
        hasEdges: !!flowData.edges,
        nodeCount: flowData.nodes?.length || 0,
        edgeCount: flowData.edges?.length || 0,
      });

      console.log("=== FULL IMPORT DATA ===", {
        nodes: flowData.nodes,
        edges: flowData.edges,
        viewport: flowData.viewport,
      });

      // Validate the imported data
      if (!flowData.nodes || !Array.isArray(flowData.nodes)) {
        throw new Error("Invalid flow data: missing or invalid nodes");
      }

      if (!flowData.edges || !Array.isArray(flowData.edges)) {
        console.warn(
          "No edges found in import data, will use empty edges array"
        );
        flowData.edges = [];
      }

      // Clear current flow
      viewModel.clearFlow();

      // Import nodes with isUpdated attribute
      const importedNodes = [];
      flowData.nodes.forEach((node, index) => {
        try {
          console.log(
            `=== IMPORTING NODE ${index + 1}/${flowData.nodes.length} ===`,
            {
              id: node.id,
              type: node.type,
              hasData: !!node.data,
              position: node.position,
              data: node.data,
            }
          );

          // Apply import transformations to handle exported format
          let transformedNode = transformLabelsForImport(node);
          transformedNode = transformSectionsForImport(transformedNode);

          // Create node with original ID by directly adding to model
          const nodeToAdd = {
            id: transformedNode.id, // Preserve original ID
            type: transformedNode.type,
            position: transformedNode.position,
            data: {
              ...transformedNode.data,
              isUpdated:
                transformedNode.isUpdated !== undefined
                  ? transformedNode.isUpdated
                  : false,
            },
          };

          // Add node directly to the model to preserve ID
          viewModel.service.model.addNode(nodeToAdd);

          // Add proper handlers to the imported node
          const importedNode = viewModel.service.model.getNode(nodeToAdd.id);
          if (importedNode) {
            // Add delete handler
            importedNode.data.onDelete = (id) => {
              console.log("Direct delete called for imported node:", id);
              viewModel.deleteNode(id);
            };

            // Add type-specific handlers
            if (nodeToAdd.type === "normalMessage") {
              importedNode.data.onChangeText = (id, value) =>
                viewModel.updateNode(id, { text: value });
            } else if (nodeToAdd.type === "interactiveMessage") {
              importedNode.data.onChangeHeader = (id, value) =>
                viewModel.updateNode(id, { header: value });
              importedNode.data.onChangeBody = (id, value) =>
                viewModel.updateNode(id, { body: value });
              importedNode.data.onChangeFooter = (id, value) =>
                viewModel.updateNode(id, { footer: value });
              importedNode.data.onChangeLabels = (id, value) =>
                viewModel.updateNode(id, { labels: value });
              importedNode.data.onChangeSections = (id, value) =>
                viewModel.updateNode(id, { sections: value });
              importedNode.data.onChangeListHeader = (id, value) =>
                viewModel.updateNode(id, { listHeader: value });
              importedNode.data.onChangeMessageType = (id, value) =>
                viewModel.updateNode(id, { messageType: value });
            } else if (nodeToAdd.type === "mediaMessage") {
              importedNode.data.onChangeFile = (id, file) =>
                viewModel.updateNode(id, { selectedFile: file });
              importedNode.data.onChangeCaption = (id, caption) =>
                viewModel.updateNode(id, { caption });
              importedNode.data.onChangeMediaType = (id, mediaType) =>
                viewModel.updateNode(id, { mediaType });
              importedNode.data.onChangeFileName = (id, fileName) =>
                viewModel.updateNode(id, { fileName });
              importedNode.data.onChangeFilePath = (id, filePath) =>
                viewModel.updateNode(id, { filePath });
              importedNode.data.onChangePreviewUrl = (id, previewUrl) =>
                viewModel.updateNode(id, { previewUrl });
            }
          }

          console.log(`=== NODE IMPORT RESULT ${index + 1} ===`, {
            originalId: node.id,
            importedId: nodeToAdd.id,
            success: true,
            importedNode: nodeToAdd,
            hasHandlers: !!importedNode?.data?.onDelete,
          });

          importedNodes.push(nodeToAdd);
        } catch (error) {
          console.error(`Failed to import node ${node.id}:`, error);
          throw new Error(`Failed to import node ${node.id}: ${error.message}`);
        }
      });

      // Notify listeners after all nodes are imported
      viewModel.notifyListeners();

      // Verify nodes were imported with correct IDs
      const importedNodesFromModel = viewModel.getNodes();
      console.log("=== NODE IMPORT VERIFICATION ===", {
        expectedNodeIds: flowData.nodes.map((n) => n.id),
        actualNodeIds: importedNodesFromModel.map((n) => n.id),
        allMatch: flowData.nodes.every(
          (node, index) => node.id === importedNodesFromModel[index]?.id
        ),
      });

      // Import edges
      const importedEdges = [];
      console.log("=== STARTING EDGE IMPORT ===", {
        totalEdges: flowData.edges.length,
        availableNodes: viewModel.getNodes().map((n) => n.id),
      });

      flowData.edges.forEach((edge, index) => {
        try {
          console.log(
            `=== IMPORTING EDGE ${index + 1}/${flowData.edges.length} ===`,
            {
              id: edge.id,
              source: edge.source,
              target: edge.target,
              sourceHandle: edge.sourceHandle,
              targetHandle: edge.targetHandle,
            }
          );

          console.log("=== EDGE IMPORT DETAILS ===", {
            edgeId: edge.id,
            source: edge.source,
            target: edge.target,
            sourceHandle: edge.sourceHandle,
            targetHandle: edge.targetHandle,
            edgeType: edge.type,
          });

          // Check if source and target nodes exist
          const sourceNode = viewModel.getNodeById(edge.source);
          const targetNode = viewModel.getNodeById(edge.target);

          console.log("=== EDGE NODE VALIDATION ===", {
            sourceExists: !!sourceNode,
            targetExists: !!targetNode,
            sourceNode: sourceNode,
            targetNode: targetNode,
          });

          // Create edge with original ID
          const edgeToAdd = {
            id: edge.id, // Preserve original ID
            source: edge.source,
            target: edge.target,
            sourceHandle: edge.sourceHandle || null,
            targetHandle: edge.targetHandle || null,
            type: edge.type || "default",
            animated: edge.animated || false,
            style: edge.style || {},
          };

          // Add edge directly to the model to preserve ID
          viewModel.service.model.addEdge(edgeToAdd);

          // Verify edge was added to model
          const allEdges = viewModel.service.model.getAllEdges();
          const addedEdge = allEdges.find((e) => e.id === edge.id);

          console.log("=== EDGE IMPORT RESULT ===", {
            originalEdgeId: edge.id,
            importedEdge: edgeToAdd,
            success: true,
            edgeAddedToModel: !!addedEdge,
            totalEdgesInModel: allEdges.length,
            allEdgeIds: allEdges.map((e) => e.id),
          });

          importedEdges.push(edgeToAdd);
        } catch (error) {
          console.warn(`Failed to import edge ${edge.id}:`, error);
        }
      });

      // Notify listeners after all edges are imported
      viewModel.notifyListeners();

      // Final verification
      const finalNodes = viewModel.getNodes();
      const finalEdges = viewModel.getEdges();

      console.log("=== FINAL IMPORT STATE ===", {
        totalNodes: finalNodes.length,
        totalEdges: finalEdges.length,
        nodeIds: finalNodes.map((n) => n.id),
        edgeIds: finalEdges.map((e) => e.id),
        edgesWithConnections: finalEdges.map((e) => ({
          id: e.id,
          source: e.source,
          target: e.target,
          sourceHandle: e.sourceHandle,
          targetHandle: e.targetHandle,
        })),
      });

      // Set viewport if available
      if (flowData.viewport) {
        setViewport(flowData.viewport);
      }

      setImportDialogOpen(false);
      setImportData("");

      setSnackbar({
        open: true,
        message: `Flow imported successfully! ${flowData.nodes.length} nodes, ${flowData.edges.length} edges`,
        severity: "success",
      });
    } catch (error) {
      console.error("Import error:", error);
      setSnackbar({
        open: true,
        message: "Failed to import flow: " + error.message,
        severity: "error",
      });
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImportData(e.target.result);
      };
      reader.readAsText(file);
    }
  };

  const onDragStart = (event, nodeType) => {
    console.log(`=== DRAG START - ${nodeType} ===`);
    event.dataTransfer.setData("application/reactflow", nodeType);
    event.dataTransfer.effectAllowed = "move";
  };

  return (
    <div style={{ display: "flex", height: "100%", overflow: "hidden" }}>
      {/* Sidebar */}
      <div
        style={{
          width: 250,
          background: "#f8f9fa",
          borderRight: "1px solid #dee2e6",
          padding: "20px",
          overflowY: "auto",
          flexShrink: 0,
        }}
      >
        <h3 style={{ marginBottom: "8px", color: "#333" }}>Components</h3>
        <p
          style={{
            marginBottom: "20px",
            color: "#666",
            fontSize: "12px",
            lineHeight: "1.4",
            fontStyle: "italic",
          }}
        >
          Design automated customer messages using a drag-and-drop builder
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {/* Default Message Node */}
          <div
            draggable
            onDragStart={(e) => onDragStart(e, "normalMessage")}
            style={{
              cursor: "grab",
              backgroundColor: "#17a2b8",
              color: "white",
              border: "none",
              padding: "12px",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              fontSize: "14px",
              fontWeight: "500",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor = "#138496")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = "#17a2b8")
            }
          >
            Default Message
          </div>

          {/* Interactive Message Node */}
          <div
            draggable
            onDragStart={(e) => onDragStart(e, "interactiveMessage")}
            style={{
              cursor: "grab",
              backgroundColor: "#fd7e14",
              color: "white",
              border: "none",
              padding: "12px",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              fontSize: "14px",
              fontWeight: "500",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor = "#e8690b")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = "#fd7e14")
            }
          >
            Interactive Message
          </div>

          {/* Media Message Node */}
          <div
            draggable
            onDragStart={(e) => onDragStart(e, "mediaMessage")}
            style={{
              cursor: "grab",
              backgroundColor: "#6f42c1",
              color: "white",
              border: "none",
              padding: "12px",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              fontSize: "14px",
              fontWeight: "500",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor = "#5a32a3")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = "#6f42c1")
            }
          >
            Media Message
          </div>

          {/* Import/Export Section */}
          <div
            style={{
              marginTop: "30px",
              borderTop: "1px solid #dee2e6",
              paddingTop: "20px",
            }}
          >
            <h4
              style={{ marginBottom: "15px", color: "#333", fontSize: "14px" }}
            >
              Flow Management
            </h4>

            <div
              style={{ display: "flex", flexDirection: "column", gap: "8px" }}
            >
              {/* Save Button */}
              <Button
                variant="outlined"
                size="small"
                startIcon={<Save />}
                onClick={handleSaveFlow}
                style={{
                  borderColor: "#ffc107",
                  color: "#ffc107",
                  fontSize: "12px",
                  padding: "8px 12px",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#ffc107";
                  e.currentTarget.style.color = "white";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.color = "#ffc107";
                }}
              >
                Save Flow
              </Button>

              {/* Export Button */}
              <Button
                variant="outlined"
                size="small"
                startIcon={<Download />}
                onClick={exportFlow}
                style={{
                  borderColor: "#28a745",
                  color: "#28a745",
                  fontSize: "12px",
                  padding: "8px 12px",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#28a745";
                  e.currentTarget.style.color = "white";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.color = "#28a745";
                }}
              >
                Export Flow
              </Button>

              {/* Import Button */}
              <Button
                variant="outlined"
                size="small"
                startIcon={<Upload />}
                onClick={() => setImportDialogOpen(true)}
                style={{
                  borderColor: "#007bff",
                  color: "#007bff",
                  fontSize: "12px",
                  padding: "8px 12px",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#007bff";
                  e.currentTarget.style.color = "white";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.color = "#007bff";
                }}
              >
                Import Flow
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Import Dialog */}
      <Dialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Import Flow</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload a JSON file or paste JSON data to import a flow:
            </Typography>

            {/* File Upload */}
            <Button
              variant="outlined"
              component="label"
              startIcon={<FolderOpen />}
              sx={{ mb: 2, mr: 2 }}
            >
              Choose File
              <input
                type="file"
                hidden
                accept=".json"
                onChange={handleFileUpload}
              />
            </Button>

            {/* JSON Text Area */}
            <textarea
              value={importData}
              onChange={(e) => setImportData(e.target.value)}
              placeholder="Paste JSON data here..."
              style={{
                width: "100%",
                minHeight: "200px",
                padding: "12px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                fontFamily: "monospace",
                fontSize: "12px",
                resize: "vertical",
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)}>Cancel</Button>
          <Button onClick={importFlow} variant="contained" color="primary">
            Import Flow
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* React Flow Canvas */}
      <div
        style={{
          flexGrow: 1,
          position: "relative",
          overflow: "visible", // Changed from "hidden" to "visible"
          zIndex: 1,
        }}
        ref={reactFlowWrapper}
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={handleNodesChange}
          onEdgesChange={handleEdgesChange}
          onConnect={onConnect}
          onDragOver={onDragOver}
          onDrop={onDrop}
          onMove={(event, viewport) => {
            console.log("=== REACTFLOW VIEWPORT MOVE ===");
            console.log("Viewport:", viewport);
            console.log("Event:", event);
            console.log(
              "Canvas wrapper:",
              reactFlowWrapper.current?.getBoundingClientRect()
            );
            console.log("=== END VIEWPORT MOVE ===");
          }}
          onViewportChange={(viewport) => {
            console.log("=== REACTFLOW VIEWPORT CHANGE ===");
            console.log("New viewport:", viewport);
            console.log(
              "Current nodes:",
              nodes.map((n) => ({ id: n.id, position: n.position }))
            );
            console.log("=== END VIEWPORT CHANGE ===");
          }}
          onNodeDragStart={(event, node) => {
            console.log("=== NODE DRAG START ===");
            console.log("Node:", {
              id: node.id,
              position: node.position,
              type: node.type,
            });
            console.log("Event:", event);
            console.log(
              "Canvas bounds:",
              reactFlowWrapper.current?.getBoundingClientRect()
            );
            console.log("=== END NODE DRAG START ===");
          }}
          onNodeDrag={(event, node) => {
            console.log("=== NODE DRAGGING ===");
            console.log("Node:", {
              id: node.id,
              position: node.position,
              type: node.type,
            });
            console.log("Event:", event);
            console.log(
              "Canvas bounds:",
              reactFlowWrapper.current?.getBoundingClientRect()
            );
            console.log("=== END NODE DRAGGING ===");
          }}
          onNodeDragStop={(event, node) => {
            console.log("=== NODE DRAG STOP ===");
            console.log("Node:", {
              id: node.id,
              position: node.position,
              type: node.type,
            });
            console.log("Event:", event);
            console.log(
              "Canvas bounds:",
              reactFlowWrapper.current?.getBoundingClientRect()
            );
            console.log("=== END NODE DRAG STOP ===");
          }}
          nodeTypes={nodeTypes}
          fitView={false} // Disabled fitView to prevent automatic viewport adjustments
          minZoom={0.1}
          maxZoom={2}
          defaultViewport={{ x: 0, y: 0, zoom: 1 }}
          style={{ width: "100%", height: "100%", zIndex: 1 }}
        >
          <Background />
          <Controls />
          <MiniMap />
        </ReactFlow>
      </div>
    </div>
  );
};

export const FlowCanvas = ({ automationId }) => {
  const viewModel = useFlowViewModel();
  const reactFlowWrapper = useRef(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        margin: 0,
        padding: 0,
        overflow: "hidden",
      }}
    >
      <ReactFlowProvider>
        <FlowCanvasInner
          viewModel={viewModel}
          reactFlowWrapper={reactFlowWrapper}
          chatbotId={automationId}
          nodes={nodes}
          setNodes={setNodes}
          edges={edges}
          setEdges={setEdges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
        />
      </ReactFlowProvider>
    </div>
  );
};
